import { shouldMqReducedMotion } from "./mq"

// Dragging down to dismiss the card a page shows above everything for a place
// tapped on its map, on touchscreens (like `SiteCardMobileWrap` in the maps
// app): the card follows the pointer down and fades out, released far enough
// it slides out and is dismissed, otherwise it springs back; taps still work.
//
// Spread `mapCardDrag(propertyPrefix)` into the hook, add
// `mapCardDragActions(target)` to its `actions` (`target` names the card's
// target) and declare its values:
// - `mapCardDraggingClass`: the card's class while dragged (no transition)
// - `mapCardDraggedAwayClass`: the card's class once dragged away, so its
//   removal animation doesn't run again
// and implement `mapCardHide()`, which asks the server to remove the card.
//
// The card's CSS applies the drag with `translate: 0 var([prefix]-y, 0)` and
// `opacity: var([prefix]-opacity, 1)`, transitioned (`translate` too, its end
// dismisses the card) except while dragging, and sets `touch-action: none`.

// px the card has to move before a press turns into a drag, so taps still work
const DRAG_THRESHOLD = 5
// px the card is dragged down by when it's dismissed (and fully faded out)
const CLOSED_Y = 200
// px it has to be dragged down past for the release to dismiss it
const CLOSE_DISTANCE = 40
// px it can be dragged up before the drag is cancelled
const CANCEL_DISTANCE = 70
// how far it moves up with the pointer, which it can't be dismissed towards
const UP_RESISTANCE = 0.2

export const mapCardDragActions = (target) => [
  { name: `mapCardDragStart`, target, type: `pointerdown` },
  { name: `mapCardDragMove`, target, type: `pointermove` },
  { name: `mapCardDragEnd`, target, type: `pointerup` },
  { name: `mapCardDragEnd`, target, type: `pointercancel` },
  { name: `mapCardNativeDrag`, target, type: `dragstart` },
  { name: `mapCardClick`, target, type: `click`, capture: true },
  { name: `mapCardTransitionEnd`, target, type: `transitionend` },
]

// `propertyPrefix`: the card's custom properties, e.g. `--regions-page-map-card`
export const mapCardDrag = (propertyPrefix) => ({
  mapCardDrag: null,
  mapCardClickSuppressed: false,
  mapCardDismissing: false,

  // a new card (another place's card patches the shown one in place)
  mapCardDragReset() {
    this.mapCardDrag = null
    this.mapCardClickSuppressed = false
    this.mapCardDismissing = false
  },

  mapCardDragStartAction(e) {
    // a drag that ended without a click must not swallow this press's click
    this.mapCardClickSuppressed = false
    if (this.mapCardDismissing || e.button !== 0) return

    this.mapCardDrag = {
      pointerId: e.pointerId,
      startY: e.clientY,
      y: 0,
      active: false,
    }
  },

  mapCardDragMoveAction(e, node) {
    const drag = this.mapCardDrag
    if (!drag || e.pointerId !== drag.pointerId) return

    // released outside the card before the drag started
    if (!drag.active && !(e.buttons & 1)) {
      this.mapCardDrag = null
      return
    }

    const deltaY = e.clientY - drag.startY

    if (!drag.active) {
      if (Math.abs(deltaY) < DRAG_THRESHOLD) return
      drag.active = true
      // only now, so a tap's click still goes to the link it's on; the
      // pointer may be gone already (released or cancelled meanwhile)
      try {
        node.setPointerCapture(e.pointerId)
      } catch {
        // dragging goes on without it, the card's events still arrive
      }
      node.classList.add(this.mapCardDraggingClassValue)
    }

    if (deltaY < -CANCEL_DISTANCE) {
      this.mapCardDragEndAction(e, node)
      return
    }

    drag.y = deltaY > 0 ? deltaY : deltaY * UP_RESISTANCE
    this.mapCardMoveTo(node, drag.y)
  },

  mapCardDragEndAction(e, node) {
    const drag = this.mapCardDrag
    if (!drag || e.pointerId !== drag.pointerId) return

    this.mapCardDrag = null
    if (node.hasPointerCapture(e.pointerId))
      node.releasePointerCapture(e.pointerId)
    if (!drag.active) return

    this.mapCardClickSuppressed = true
    // in the same style change as the class removal, which turns the
    // transition back on, so the card eases to where it goes
    node.classList.remove(this.mapCardDraggingClassValue)

    if (e.type === `pointerup` && drag.y > CLOSE_DISTANCE) {
      this.mapCardDismiss(node)
    } else {
      this.mapCardMoveTo(node, 0)
    }
  },

  // links and images would start a native drag, which cancels the pointer
  mapCardNativeDragAction(e) {
    e.preventDefault()
  },

  // the click ending a drag must not open the place it was released on
  mapCardClickAction(e) {
    if (!this.mapCardClickSuppressed) return

    this.mapCardClickSuppressed = false
    e.preventDefault()
    e.stopPropagation()
  },

  mapCardTransitionEndAction(e, node) {
    if (e.target !== node || e.propertyName !== `translate`) return
    if (this.mapCardDismissing) this.mapCardHide()
  },

  // `y`: px below its place, it fades out towards `CLOSED_Y`
  mapCardMoveTo(node, y) {
    const opacity = 1 - Math.min(Math.max(y / CLOSED_Y, 0), 1)

    if (y) {
      node.style.setProperty(`${propertyPrefix}-y`, `${y}px`)
      node.style.setProperty(`${propertyPrefix}-opacity`, opacity)
    } else {
      node.style.removeProperty(`${propertyPrefix}-y`)
      node.style.removeProperty(`${propertyPrefix}-opacity`)
    }
  },

  // slides out down, then is removed (without its removal animation, as it's
  // already out); right away when there's no transition to wait for
  mapCardDismiss(node) {
    this.mapCardDismissing = true
    node.classList.add(this.mapCardDraggedAwayClassValue)
    this.mapCardMoveTo(node, CLOSED_Y)

    if (shouldMqReducedMotion()) this.mapCardHide()
  },
})
